<template>
  <v-layout row justify-center align-center>
    <v-flex xs12>
      <v-card>
        <errors-list :errorMessage="error.errorMessage" />
        <v-form ref="form" @submit.prevent="onSubmit" color="green">
          <v-card-text>
            <v-text-field
              v-model="user.firstName"
              color="green darken-1"
              :label="$t('@employees/firstNameLabel')"
              name="firstName"
              :disabled="submitting"
              :error="!user.firstName"
              :error-messages="validationError(error.errorField, errorFieldMap.firstName)"
            />
            <v-text-field
              v-model="user.lastName"
              color="green darken-1"
              :label="$t('@employees/lastNameLabel')"
              name="lastName"
              :disabled="submitting"
              :error="!user.lastName"
              :error-messages="validationError(error.errorField, errorFieldMap.lastName)"
            />
            <v-text-field
              v-model="user.email"
              color="green darken-1"
              :label="$t('@employees/email')"
              disabled
              name="email"
              :error-messages="validationError(error.errorField, errorFieldMap.email)"
            />
            <v-text-field
              v-model="user.phone"
              color="green darken-1"
              :label="$t('@employees/phone')"
              mask="0## ### ## ##"
              placeholder="070 XXX XX XX"
              :disabled="submitting"
              name="phone"
              :error-messages="validationError(error.errorField, errorFieldMap.phone)"
            />

            <v-layout row justify-center align-center>
              <!-- Hidden until next iteration -->
              <!-- <v-flex xs6>
                <v-list subheader dense color="green darken-1">
                  <v-subheader>Group rights</v-subheader>
                  <v-list-tile :key="userGroup.id" v-for="userGroup in allUserGroups">
                    <v-list-tile-action>
                      <v-checkbox
                        :label="userGroup.name"
                        color="green darken-1"
                        :disabled="submitting"
                        v-model="userGroup.checked"
                      />
                    </v-list-tile-action>
                  </v-list-tile>
                </v-list>
              </v-flex> -->

              <v-flex xs12>
                <v-list subheader dense color="green darken-1">
                  <v-subheader>{{ $t('@employees/productGroupsRights') }}</v-subheader>
                  <v-list-tile
                    :key="userProductGroup.id"
                    v-for="userProductGroup in allUserProductGroups"
                  >
                    <v-list-tile-action>
                      <v-checkbox
                        :label="userProductGroup.name"
                        color="green darken-1"
                        v-model="userProductGroup.checked"
                      />
                    </v-list-tile-action>
                  </v-list-tile>
                </v-list>
              </v-flex>
            </v-layout>
          </v-card-text>

          <v-divider />

          <v-card-actions>
            <v-spacer />
            <v-btn :disabled="submitting" color="red darken-1" flat @click="closeModal()">
              {{ $t('@form/cancel') }}
            </v-btn>
            <v-btn type="submit" :disabled="submitting" color="green darken-1" flat>
              {{ $t('@form/save') }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import { phoneRegexp, phonePrefixHardcode } from '../../utils';
import { getErrorMessage, validationError, getErrorObject } from '../../errorHandlers';
import ErrorsList from '../../components/ErrorsList';

const errorFieldMap = {
  firstName: 'firstName',
  lastName: 'lastName',
  email: 'email',
  phone: 'phone',
};

export default {
  components: { ErrorsList },
  props: {
    userId: {
      type: Number,
    },
  },

  data() {
    return {
      submitting: false,
      error: {
        errorMessage: '',
        errorField: '',
      },
      user: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
      },
      allUserGroups: [],
      allUserProductGroups: [],
      validationError,
      errorFieldMap,
    };
  },

  async mounted() {
    if (this.userId) {
      await this.fetchUser();
    }
  },

  methods: {
    closeModal(isSent = false) {
      this.$emit('close-modal', isSent);
    },

    async fetchUser() {
      try {
        this.submitting = true;
        const { data = {} } = await this.$http.get(`/api/employees/${this.userId}`);
        const {
          allUserGroups = [],
          allProductGroups = [],
          userGroups = [],
          userProductGroups = [],
          ...user
        } = data;
        this.allUserGroups = allUserGroups.map((g) => {
          return { ...g, checked: !!userGroups.find((id) => g.id === id) };
        });
        this.allUserProductGroups = allProductGroups.map((p) => {
          return { ...p, checked: !!userProductGroups.find((id) => p.id === id) };
        });

        user.phone = (user.phone || '').replace(phonePrefixHardcode, '0');

        this.user = user;
      } catch (e) {
        this.error.errorMessage = getErrorMessage(e);
      } finally {
        this.submitting = false;
      }
    },

    isPhoneValid(phone) {
      return phoneRegexp.test(phone);
    },

    validateForm() {
      const { firstName, lastName, phone } = this.user;

      if (!firstName) {
        return {
          errorMessage: 'Field "First name" is required',
          errorField: errorFieldMap.firstName,
        };
      }
      if (!lastName) {
        return {
          errorMessage: 'Field "Last name" is required',
          errorField: errorFieldMap.lastName,
        };
      }
      if (phone && !this.isPhoneValid(phone)) {
        return {
          errorMessage: 'Phone should satisfy the requirements.',
          errorField: errorFieldMap.phone,
        };
      }
      return false;
    },

    async onSubmit() {
      const { id, firstName, lastName, phone } = this.user;
      try {
        const error = this.validateForm();
        if (error) {
          this.error = error;
          return;
        }
        this.submitting = true;

        await this.$http.put(`/api/employees/${id}`, {
          firstName,
          lastName,
          phone,
          userGroups: this.allUserGroups.filter((g) => g.checked).map((g) => g.id),
          userProductGroups: this.allUserProductGroups.filter((p) => p.checked).map((p) => p.id),
        });
        this.closeModal(true);
        this.error = { errorMessage: '', errorField: '' };
      } catch (e) {
        this.error = getErrorObject(e);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
